import { useState, useCallback } from 'react';

interface UseBulkSelectionOptions {
  items: Array<{ id: string }>;
  onBulkAction?: (selectedIds: string[]) => void;
}

interface UseBulkSelectionReturn {
  selectedIds: string[];
  isAllSelected: boolean;
  selectedCount: number;
  handleSelectAll: () => void;
  handleSelectItem: (id: string) => void;
  handleBulkAction: () => void;
  isSelected: (id: string) => boolean;
  clearSelection: () => void;
}

export const useBulkSelection = ({ 
  items, 
  onBulkAction 
}: UseBulkSelectionOptions): UseBulkSelectionReturn => {
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [isAllSelected, setIsAllSelected] = useState(false);

  const selectedCount = selectedIds.length;

  const handleSelectAll = useCallback(() => {
    if (isAllSelected) {
      setSelectedIds([]);
      setIsAllSelected(false);
    } else {
      const allIds = items.map(item => item.id);
      setSelectedIds(allIds);
      setIsAllSelected(true);
    }
  }, [isAllSelected, items]);

  const handleSelectItem = useCallback((id: string) => {
    setSelectedIds(prevSelectedIds => {
      if (prevSelectedIds.includes(id)) {
        const newSelectedIds = prevSelectedIds.filter(selectedId => selectedId !== id);
        setIsAllSelected(false);
        return newSelectedIds;
      } else {
        const newSelectedIds = [...prevSelectedIds, id];
        setIsAllSelected(newSelectedIds.length === items.length);
        return newSelectedIds;
      }
    });
  }, [items.length]);

  const handleBulkAction = useCallback(() => {
    if (onBulkAction && selectedIds.length > 0) {
      onBulkAction(selectedIds);
    }
  }, [onBulkAction, selectedIds]);

  const isSelected = useCallback((id: string) => {
    return selectedIds.includes(id);
  }, [selectedIds]);

  const clearSelection = useCallback(() => {
    setSelectedIds([]);
    setIsAllSelected(false);
  }, []);

  return {
    selectedIds,
    isAllSelected,
    selectedCount,
    handleSelectAll,
    handleSelectItem,
    handleBulkAction,
    isSelected,
    clearSelection
  };
};
